import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import {
  DrawDetail,
  fmtMoney,
} from "Pages/Admin/ProjectDetail/sections/DrawHistory/DrawHistory.types";
import { formatDate } from "utils/Common/Date";

export interface G702PdfProps {
  drawDetail: DrawDetail;
  projectName?: string;
}

// Same palette as DrawHistoryPdf.tsx (lifted from App.scss :root vars) so
// every export in this section reads as one consistent document family.
const COLORS = {
  b: "#14508c",
  bdk: "#0d3a66",
  bd: "#dde1e6",
  tx: "#111111",
  tx2: "#555c66",
  tx3: "#909399",
  green: "#1a6b35",
  amber: "#7a5c00",
  red: "#a32d2d",
};

const S = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 40,
    paddingHorizontal: 36,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.tx,
    backgroundColor: "#ffffff",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 15,
    color: COLORS.b,
    marginBottom: 4,
  },
  headerSub: { fontSize: 8.5, color: COLORS.tx2 },
  headerRight: { alignItems: "flex-end" },
  headerMetaLine: { fontSize: 8, color: COLORS.tx2, marginBottom: 2 },

  card: {
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 18,
    backgroundColor: "#ffffff",
  },
  cardTitleRow: {
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  cardTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    color: COLORS.tx,
    letterSpacing: 0.5,
  },

  g702Line: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 7,
    paddingHorizontal: 4,
    borderBottomWidth: 0.75,
    borderBottomColor: COLORS.bd,
  },
  g702LineTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 9,
    paddingHorizontal: 4,
    borderTopWidth: 1.5,
    borderTopColor: COLORS.b,
    marginTop: 4,
  },
  g702Left: { flexDirection: "row", alignItems: "center", flex: 1 },
  g702Num: {
    width: 30,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.tx3,
  },
  g702Label: { fontSize: 9.5, color: COLORS.tx2, flex: 1 },
  g702LabelBold: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9.5,
    color: COLORS.tx,
  },
  g702Val: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    width: 130,
  },
  g702ValTotal: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    width: 130,
  },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 36,
    right: 36,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.bd,
    paddingTop: 6,
  },
  footerText: { fontSize: 7.5, color: COLORS.tx3 },
});

const toneColor = (tone?: "blue" | "green" | "amber" | "red") => {
  switch (tone) {
    case "blue":
      return COLORS.b;
    case "green":
      return COLORS.green;
    case "amber":
      return COLORS.amber;
    case "red":
      return COLORS.red;
    default:
      return COLORS.tx;
  }
};

export default function G702Pdf({ drawDetail, projectName }: G702PdfProps) {
  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const { draw, summary: g702 } = drawDetail;
  const drawLabel = String(draw.drawNumber).padStart(2, "0");
  const retainagePct = g702?.retainageRate
    ? parseFloat(g702.retainageRate)
    : null;

  // Mirrors SummaryTab.tsx's row set exactly (including its 4a/4b/4 repeat
  // of totalCompletedAndStored) so this PDF matches the on-screen card.
  const g702Rows: {
    num: string;
    label: string;
    val: string;
    tone?: "blue" | "green" | "amber" | "red";
    isTotal?: boolean;
  }[] = [
    {
      num: "1",
      label: "Original contract sum",
      val: fmtMoney(g702?.originalContractSum),
    },
    {
      num: "1a",
      label: "SOV update / final contract",
      val: fmtMoney(g702?.sovUpdate),
    },
    {
      num: "2",
      label: "Net change by change orders",
      val: fmtMoney(g702?.netChangeByChangeOrders),
    },
    {
      num: "3",
      label: "Contract sum to date (1+1a+2)",
      val: fmtMoney(g702?.contractSumToDate),
      tone: "blue",
    },
    {
      num: "4a",
      label: "Total work completed to date",
      val: fmtMoney(g702?.totalCompletedAndStored),
    },
    {
      num: "4b",
      label: "Materials presently stored (Col F)",
      val: fmtMoney(g702?.totalCompletedAndStored),
      tone: "amber",
    },
    {
      num: "4",
      label: "Total completed & stored to date",
      val: fmtMoney(g702?.totalCompletedAndStored),
    },
    {
      num: "5",
      label: `Retainage${retainagePct != null ? ` — ${retainagePct}% of completed work` : ""}`,
      val: fmtMoney(g702?.totalRetainage),
      tone: "red",
    },
    {
      num: "6",
      label: "Total earned less retainage (4−5)",
      val: fmtMoney(g702?.totalEarnedLessRetainage),
    },
    {
      num: "7",
      label: "Less previous certificates for payment",
      val: fmtMoney(g702?.lessPreviousCertificates),
    },
    {
      num: "8",
      label: "Current payment due (6−7)",
      val: fmtMoney(g702?.currentPaymentDue),
      tone: "green",
      isTotal: true,
    },
    {
      num: "9",
      label: "Balance to finish including retainage",
      val: fmtMoney(g702?.balanceToFinish),
      tone: "amber",
    },
  ];

  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.headerRow}>
          <View>
            <Text style={S.headerTitle}>
              AIA G702 — Application &amp; Certificate for Payment
            </Text>
            <Text style={S.headerSub}>
              Application #{drawLabel}
              {draw.toDate
                ? ` · Period to ${formatDate(draw.toDate, false)}`
                : ""}
            </Text>
          </View>
          <View style={S.headerRight}>
            {projectName ? (
              <Text
                style={[
                  S.headerMetaLine,
                  { fontFamily: "Helvetica-Bold", color: COLORS.tx },
                ]}
              >
                {projectName}
              </Text>
            ) : null}
            <Text style={S.headerMetaLine}>Generated: {today}</Text>
          </View>
        </View>

        <View style={S.card}>
          <View style={S.cardTitleRow}>
            <Text style={S.cardTitle}>APPLICATION SUMMARY</Text>
          </View>
          {g702Rows.map((line) => (
            <View
              key={line.num}
              style={line.isTotal ? S.g702LineTotal : S.g702Line}
              wrap={false}
            >
              <View style={S.g702Left}>
                <Text style={S.g702Num}>{line.num}</Text>
                <Text style={line.isTotal ? S.g702LabelBold : S.g702Label}>
                  {line.label}
                </Text>
              </View>
              <Text
                style={[
                  line.isTotal ? S.g702ValTotal : S.g702Val,
                  { color: toneColor(line.tone) },
                ]}
              >
                {line.val}
              </Text>
            </View>
          ))}
        </View>

        <View style={S.footer} fixed>
          <Text style={S.footerText}>
            CSC New England · AIA G702 · Draw #{drawLabel} · {today}
          </Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
